import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { AnalysisService } from '@app/features/old-analysis/services/analysis.service';
import { Location } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

@Component({
  selector: 'app-preview-layout',
  standalone: true,
  imports: [RouterOutlet, TranslateModule],
  template: `
    <div class="preview-layout">
      <!-- Common Header for all preview pages -->
      <div class="preview-header">
         <button class="close-btn" (click)="goBack()">
          <!-- <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg> -->
          <img src="/public/Icons/back-circle.svg" alt="back-circle" height="40px" width="40px">
        </button>
        @if(showDownloadButton){
        <button
          class="download-btn"
          [disabled]="isDownloading"
          (click)="handleDownloadPDF()">

          @if(!isDownloading){
            <ng-container>
              <!-- <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7,10 12,15 17,10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg> -->
              <img src="/public/Icons/download.svg" alt="download" height="20px" width="20px">
              {{ 'preview.downloadPdf' | translate }}
            </ng-container>
          }
          @else{
            <ng-template>
              <span class="spinner"></span>
              {{ 'preview.generatingPdf' | translate }}
            </ng-template>
          }
        </button>
        }

      </div>

      <div class="preview-content">
        <router-outlet></router-outlet>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .preview-layout {
      width: 100vw;
      height: 100vh;
      background: black;
      display: flex;
      flex-direction: column;
    }

    .preview-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8rem 6rem 3rem;
      background: black;
      position: sticky;
      top: 0;
      z-index: 1000;
      flex-shrink: 0;
    }

    .download-btn, .close-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 1.5rem 3rem;
      border: none;
      border-radius: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
    }

    .download-btn {
      background: #84BC47;
      color: white;
      padding: 1.5rem 3rem;
      border-radius: 1rem;
      font-size: 1.8rem;
      cursor: pointer;
      border: none;

      img {
        filter: brightness(0) saturate(100%) invert(93%) sepia(0%) saturate(7466%) hue-rotate(74deg) brightness(109%) contrast(108%);
      }
    }

    .close-btn {
      background: transparent;
      // color: white;
      // border: 1px solid white;
      border-radius: 50%;
      padding: 1rem;
      cursor: pointer;
    }

    .preview-content {
      padding: 4rem;
      overflow-y: auto;
      background: black;
      flex: 1;
    }

    .download-btn[disabled] {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .spinner {
      width: 18px;
      height: 18px;
      border: 3px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    ::ng-deep {
      html[dir="rtl"]{
        .close-btn {
          img {
            transform: rotate(180deg);
          }
        }
      }
    }
  `]
})
export class PreviewLayoutComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private analysisService = inject(AnalysisService);
  public readonly location = inject(Location);
  private region: string | null = null;
  private landId: number | null = null;
  isDownloading: boolean = false;
  showDownloadButton: boolean = false;

  ngOnInit() {
    this.activatedRoute.params.subscribe(params => {
      this.region = params['region'] || null;
      this.landId = params['landId'] ? Number(params['landId']) : null;

      if (this.region && this.landId) {
        this.analysisService.setCurrentSelection(
          this.region,
          this.landId,
          `Land ${this.landId}`
        );
      }
    });

    const segments = this.router.url.split('/').filter(s => s);
    this.showDownloadButton =
      segments.length === 4 &&
      segments[0] === 'preview' &&
      segments[3] === 'hbu';
  }

  async handleDownloadPDF() {
    this.isDownloading = true;

    try {
      // Wait for content to fully render
      await this.waitForRender();

      const source = document.querySelector('.preview-content') as HTMLElement;
      if (!source) {
        // console.error('Preview content not found');
        this.isDownloading = false;
        return;
      }

      // Get all page wrappers
      const pageWrappers = source.querySelectorAll('.preview-page-wrapper');

      if (pageWrappers.length === 0) {
        // console.error('No page wrappers found');
        this.isDownloading = false;
        return;
      }

      // A4 dimensions in mm
      const PDF_WIDTH = 210;
      const PDF_HEIGHT = 297;

      // Create PDF with A4 format
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      // Process each page wrapper
      for (let i = 0; i < pageWrappers.length; i++) {
        const pageWrapper = pageWrappers[i] as HTMLElement;

        // Add new page for subsequent pages
        if (i > 0) {
          pdf.addPage();
        }

        // Convert page to canvas with high quality
        const canvas = await html2canvas(pageWrapper, {
          scale: 2, // Higher scale for better quality
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: pageWrapper.scrollWidth,
          height: pageWrapper.scrollHeight,
          windowWidth: pageWrapper.scrollWidth,
          windowHeight: pageWrapper.scrollHeight
        });

        // Convert canvas to image data
        const imgData = canvas.toDataURL('image/png', 1.0);

        // Calculate dimensions to fit A4 exactly
        const imgWidth = PDF_WIDTH;
        const pageHeight = PDF_HEIGHT;
        const imgHeight = (canvas.height * PDF_WIDTH) / canvas.width;

        // If image is taller than A4, scale it down
        let finalWidth = imgWidth;
        let finalHeight = imgHeight;

        if (imgHeight > pageHeight) {
          finalHeight = pageHeight;
          finalWidth = (canvas.width * pageHeight) / canvas.height;
        }

        // Center the image on the page
        const xOffset = (PDF_WIDTH - finalWidth) / 2;
        const yOffset = 0;

        // Add image to PDF
        pdf.addImage(
          imgData,
          'PNG',
          xOffset,
          yOffset,
          finalWidth,
          finalHeight,
          undefined,
          'FAST'
        );
      }

      // Save the PDF
      const fileName = `preview-export-${Date.now()}.pdf`;

      pdf.save(fileName);
      this.downloadStaticPdf();

    } catch (error) {
      // console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      this.isDownloading = false;
    }
  }

  private waitForRender(): Promise<void> {
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => resolve(), 500);
        });
      });
    });
  }

  private downloadStaticPdf() {
    const link = document.createElement('a');
    link.href = '/public/hbu-app.pdf';
    link.download = 'hbu-app.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }


  goBack() {
    this.location.back()

    // if (this.region && this.landId && page) {
    //   this.router.navigate(['/main/analysis-configuration', this.region, this.landId, page]);
    // } else {
    //   this.router.navigate(['/main/analysis-configuration']);
    // }
  }
}
